import { Star, Quote } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const TestimonialsSection = ({ config }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Default testimonials si no hay config
  const defaultTestimonials = [
    {
      name: "María González",
      company: "Empresaria",
      text: "Excelente servicio! Me ayudaron con mi RFC y pasaporte en tiempo récord. El seguimiento fue impecable y siempre estuvieron disponibles para resolver mis dudas.",
      rating: 5
    },
    {
      name: "Carlos Ramírez",
      company: "Ingeniero",
      text: "Después de intentar hacer mi trámite por mi cuenta durante meses, ellos lo resolvieron en semanas. Muy profesionales y confiables. 100% recomendados.",
      rating: 5
    }
  ];

  const testimonials = (config && config.testimonials && config.testimonials.length > 0)
    ? config.testimonials
    : defaultTestimonials;

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-blue-200 rounded-full opacity-10 blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-purple-200 rounded-full opacity-10 blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`text-center mb-16 transition-all duration-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
        }`}>
          <div className="inline-flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full mb-4">
            <span className="text-yellow-700 font-semibold text-sm">TESTIMONIOS</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Lo que dicen{" "}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              nuestros clientes
            </span>
          </h2>
          <p className="text-xl text-gray-600">
            Casos de éxito reales con resultados comprobados
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              testimonial={testimonial}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const TestimonialCard = ({ testimonial, index, isVisible }) => {
  const delay = `${index * 200}ms`;

  return (
    <div
      className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
      }`}
      style={{ transitionDelay: delay }}
    >
      {/* Gradient background on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-purple-500/0 to-pink-500/0 group-hover:from-blue-500/5 group-hover:via-purple-500/5 group-hover:to-pink-500/5 transition-all duration-500"></div>

      <div className="relative z-10">
        {/* Quote icon */}
        <div className="flex items-center justify-between mb-6">
          <Quote className="w-12 h-12 text-blue-600/20 transform group-hover:scale-110 group-hover:text-purple-600/30 transition-all duration-500" />
          
          {/* Star rating */}
          <div className="flex gap-1">
            {[...Array(testimonial.rating || 5)].map((_, i) => (
              <Star
                key={i}
                className="w-5 h-5 text-yellow-400 fill-yellow-400 transform group-hover:scale-110 transition-transform duration-300"
                style={{ transitionDelay: `${i * 50}ms` }}
              />
            ))}
          </div>
        </div>

        <p className="text-gray-700 text-lg mb-8 leading-relaxed italic">
          &quot;{testimonial.text}&quot;
        </p>

        <div className="flex items-center">
          {/* Avatar */}
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl mr-4 shadow-lg transform group-hover:scale-110 transition-transform duration-300">
            {testimonial.name[0]}
          </div>
          
          <div>
            <div className="font-bold text-gray-900 text-lg group-hover:text-blue-600 transition-colors duration-300">
              {testimonial.name}
            </div>
            <div className="text-gray-600">{testimonial.company}</div>
          </div>
        </div>
      </div>

      {/* Decorative corner element */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-yellow-400/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
    </div>
  );
};

export default TestimonialsSection;